import { Fragment, useEffect, useState } from "react";
import { useAuth } from "../../../../auth/AuthenticationWrapper";
import AssetsService from "../../../../rest/assetsService";
import { toast } from "react-toastify";
import InfoField from "../../../../components/lib/infoField";
import CustomButton from "../../../../components/lib/customButton";
import ConfirmDeleteModal from "../../../../components/reusables/confirmDeleteModal";
import { formatCurrency } from "../../../../utils/CurrencyUtils";
import NutritionalFactsTableView from "../../../../components/reusables/nutritionalFactsTableView";
import NoPictureWarning from "../../../../resources/nopic.png";
import ModalInfo from "../../../../components/lib/modalInfo";
import { GrCircleQuestion } from "react-icons/gr";
import ComboService from "../../../../rest/comboService";
import ComboEditStepper from "./comboEditStepper";
import ConfirmCloseView from "../../../customers/components/confirmCloseView";
import Modal from "../../../../components/lib/modal";
import { useComboLiveUpdate } from "../../hooks/combos/useComboLiveUpdate";
import { Transition } from "@headlessui/react";
import SmartImage from "../../../../components/reusables/smartImage";

const ComboDetailsModalContent = ({ combo, onEdit, onDelete }) => {
    const liveCombo = useComboLiveUpdate(combo);
    const { hasPermission } = useAuth();

    const isParent = sessionStorage.getItem('role') === 'PARENT';

    const canEdit = hasPermission("menu", "update") && !isParent;
    const canDelete = hasPermission("menu", "delete") && !isParent;

    const [imageData, setImageData] = useState(null);
    const [items, setItems] = useState([]);
    const [showModalDelete, setShowModalDelete] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [hasData, setHasData] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);

    const requestClose = () => {
        if (hasData) setConfirmClose(true);
        else setShowEditModal(false);
    };

    const forceClose = () => {
        setConfirmClose(false);
        setShowEditModal(false);
    };

    useEffect(() => {
        const getItems = () => {
            if (!liveCombo?.itemsList) {
                setItems([]);
                return;
            }
            const parsed = liveCombo.itemsList.split(',').map(raw => {
                const parts = raw.split('x').map(p => p.trim());
                if (parts.length < 2) return null;
                const name = parts[0];
                const amount = Number(parts[1]);
                const type = parts[2] || "unknown";
                if (isNaN(amount)) return null;
                return { name, amount, type };
            }).filter(Boolean);
            setItems(parsed);
        };

        getItems();
    }, [liveCombo]);

    const imageUrl = `/foodz/api/v1/uploads/carregarImagemDoProduto?path=${encodeURIComponent(liveCombo?.imageUrl)}`;

    const handleDelete = async () => {
        try {
            await ComboService.deleteCombo(liveCombo.combo_id);
            toast.success("Combo removido com sucesso!");
            setShowModalDelete(false);
            if (onDelete) onDelete();
        } catch (error) {
            console.error(error);
            toast.error("Erro ao remover combo.");
        }
    };

    const handleEditSubmit = async (updatedData) => {
        try {
            await ComboService.updateCombo(liveCombo.combo_id, updatedData);
            toast.success('Combo atualizado com sucesso!');
            setShowEditModal(false);
        } catch (error) {
            console.error(error);
            toast.error('Erro ao atualizar combo.');
        }
    };

    return (
        <Transition
            appear
            show={true}
            as={Fragment}
            enter="transition ease-out duration-300"
            enterFrom="opacity-0 scale-95 translate-y-4"
            enterTo="opacity-100 scale-100 translate-y-0"
        >
            <div className="flex flex-col h-[75vh] md:h-[650px] max-h-[80vh]">
                <div className="flex-1 overflow-y-auto px-4 py-2 sm:px-2">
                    <div className="flex flex-col lg:flex-row gap-6">
                        <div className="w-full lg:w-5/12 space-y-4">
                            <div className="w-full aspect-[5/5] sm:aspect-video lg:aspect-[5/5] bg-gray-200 rounded-2xl overflow-hidden border border-gray-200 shadow-md relative group">
                                {liveCombo?.imageUrl ? (
                                    <SmartImage
                                        src={imageUrl}
                                        alt={liveCombo.name}
                                        isThumb={false}
                                        className="object-cover group-hover:scale-105"
                                    />
                                ) : (
                                    <img src={NoPictureWarning} className="absolute inset-0 w-full h-full opacity-40 object-contain p-12" alt="N/A" />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-100" />

                                {!isParent && (
                                    <div className={`absolute top-3 left-3 z-20 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1 ${liveCombo.active ? "bg-green-500" : "bg-red-500"}`}>
                                        {liveCombo.active ? "ATIVO" : "INATIVO"}
                                    </div>
                                )}

                                <div className="absolute bottom-0 left-0 w-full p-4 z-10">
                                    <h2 className="text-white font-bold text-xl drop-shadow-lg truncate">
                                        {liveCombo.name}
                                    </h2>
                                </div>
                            </div>

                            <div className={`grid ${isParent ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                                {!isParent ?
                                    <>
                                        <div className="p-3 bg-custom_blue/5 rounded-xl border border-custom_blue/10">
                                            <p className="text-[10px] uppercase text-custom_blue font-bold mb-1">Preço</p>
                                            <p className="text-lg font-bold text-slate-800">{formatCurrency(liveCombo.price)}</p>
                                        </div>
                                        <div className="p-3 bg-custom_blue/5 rounded-xl border border-custom_blue/10">
                                            <p className="text-[10px] uppercase text-custom_blue font-bold mb-1">Preço Online</p>
                                            <p className="text-lg font-bold text-slate-800">{formatCurrency(liveCombo.online_price)}</p>
                                        </div>
                                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                                            <p className="text-[10px] uppercase text-slate-500 font-bold mb-1">Estoque</p>
                                            <p className="text-lg font-bold text-slate-800">{liveCombo.quantity}</p>
                                        </div>

                                    </>
                                    : <>
                                        <div className="p-3 bg-custom_blue/5 rounded-xl border border-custom_blue/10">
                                            <p className="text-[10px] uppercase text-custom_blue font-bold mb-1">Preço</p>
                                            <p className="text-lg font-bold text-slate-800">{formatCurrency(liveCombo.online_price)}</p>
                                        </div>
                                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                                            <p className="text-[10px] uppercase text-slate-500 font-bold mb-1">Estoque</p>
                                            <p className="text-lg font-bold text-slate-800">{liveCombo.quantity}</p>
                                        </div>
                                    </>}
                            </div>

                            {!isParent && (
                                <InfoField
                                    label="Disponível para preparo"
                                    value={liveCombo.quantity}
                                    labelExtra={<ModalInfo icon={<GrCircleQuestion />} content="Valor calculado..." />}
                                />
                            )}

                            {!isParent && (
                                <div className="py-4">
                                    <p className="flex text-base text-custom_gray_font font-semibold">Lista de Itens</p>
                                    <div className="w-full max-h-64 p-1.5 text-sm font-medium text-custom_gray_font border border-solid border-gray-300 rounded overflow-y-auto">
                                        {Array.isArray(items) && items.length > 0 ?
                                            items.map((item, index) => (
                                                <div className='flex items-center justify-between border-b-2 border-slate-200 my-1 p-1 px-2' key={index}>
                                                    <p className='font-medium'>
                                                        {item.name} x{item.amount}
                                                        <span className="ml-2 text-xs text-gray-400">
                                                            ({item.type === "product" || item.type === "xproduct" ? "Produto" : "Receita"})
                                                        </span>
                                                    </p>
                                                </div>
                                            )) : <p className="text-center">Nenhum ingrediente encontrado.</p>
                                        }
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="w-full lg:w-7/12 flex flex-col gap-4">
                            {!isParent && <InfoField label="SKU" value={liveCombo.sku || "Não registrado"} />}
                            <InfoField label="Descrição" value={liveCombo.description || "Não registrado"} />
                            {!isParent && <InfoField label="Observações" value={liveCombo.observations || "Não registrado"} />}
                            {!isParent && <InfoField label="Ativo no cardápio? " value={liveCombo.active ? " Sim" : " Não"} />}
                            {!isParent && <InfoField label="Precisa de Preparo?" value={liveCombo.needsPreparation ? " Sim" : " Não"} />}

                            <div className="py-2">
                                <NutritionalFactsTableView nutritionalFactsData={liveCombo.nutritionalFacts} />
                            </div>
                        </div>
                    </div>
                </div>

                {!isParent && (
                    <div className="mt-auto border-t border-gray-200 bg-gray-50 p-3 sm:p-4 rounded-b-lg">
                        <div className="grid grid-cols-2 sm:flex sm:justify-end gap-3">
                            <CustomButton
                                buttonType="edit"
                                text="Editar"
                                onClick={() => setShowEditModal(true)}
                                disabled={!canEdit}
                            />
                            <CustomButton
                                buttonType="delete"
                                text="Excluir"
                                onClick={() => setShowModalDelete(true)}
                                disabled={!canDelete}
                            />
                        </div>
                    </div>
                )}

                {showEditModal && !isParent && (
                    <Modal isOpen={showEditModal} setIsOpen={requestClose} title="Editar Combo" hideFooter maxWidth="max-w-xl lg:max-w-2xl md:-mr-16">
                        <div className="relative h-full flex flex-col">
                            <ComboEditStepper
                                combo={liveCombo}
                                initialImagePreview={imageData}
                                onFinish={async ({ comboValues, image }) => {
                                    try {
                                        let imageUrl = liveCombo.imageUrl;
                                        if (image) {
                                            const formData = new FormData();
                                            formData.append("file", image);
                                            const upload = await AssetsService.uploadProductImage(formData);
                                            imageUrl = upload.data;
                                        }
                                        const ingredientsString = comboValues.items.map(item => `${item.name} x${item.amount} x${item.type}`).join(', ');
                                        const finalPayload = {
                                            combo_id: liveCombo.combo_id,
                                            name: comboValues.name,
                                            price: comboValues.price,
                                            online_price: comboValues.online_price,
                                            sku: comboValues.sku,
                                            category: comboValues.category,
                                            description: comboValues.description,
                                            observations: comboValues.observations,
                                            active: comboValues.active,
                                            needsPreparation: comboValues.needsPreparation,
                                            nutritionalFacts: JSON.stringify(comboValues.nutrition),
                                            imageUrl: imageUrl,
                                            itemsList: ingredientsString,
                                            clientCode: sessionStorage.getItem("clientCode")
                                        };
                                        await handleEditSubmit(finalPayload);
                                    } catch (error) {
                                        console.error("Erro ao processar edição:", error);
                                        toast.error("Erro ao atualizar combo.");
                                    }
                                }}
                                onCancel={requestClose}
                                onDirty={() => setHasData(true)}
                            />
                            {confirmClose && (
                                <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm p-4 rounded-lg">
                                    <ConfirmCloseView onCancel={() => setConfirmClose(false)} onConfirm={forceClose} />
                                </div>
                            )}
                        </div>
                    </Modal>
                )}

                <ConfirmDeleteModal
                    isOpen={showModalDelete}
                    title="Excluir Combo"
                    message={`Tem certeza que deseja excluir ${liveCombo.name}?`}
                    warning="Essa ação não poderá ser desfeita."
                    onConfirm={handleDelete}
                    onCancel={() => setShowModalDelete(false)}
                />
            </div>
        </Transition>
    );
};

export default ComboDetailsModalContent;